import {
  Alert,
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Collapse,
  Divider,
  NativeSelect,
  Pill,
  PillsInput,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconChevronDown,
  IconChevronUp,
  IconCircle,
  IconCircleCheck,
  IconCircleMinus,
  IconCircleX,
  IconShieldCheck,
} from '@tabler/icons-react';
import CodeEditor from '@uiw/react-textarea-code-editor';
import DOMPurify from 'dompurify';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FIELD_TYPES,
  type FieldType,
  getFieldTypeMeta,
  isValidFieldType,
} from '../../../lib/form-field-types';
import { renderInputField } from '../../../lib/form-spec-to-html';
import type { FormField, FormSettings, FormSpec } from '../../../lib/form-specs';

export interface FieldPropertiesChange {
  fieldName: string;
  fieldKey: keyof FormField;
  value: string | boolean | number | string[] | undefined;
}

export interface FieldNameChange {
  oldFieldName: string;
  newFieldName: string;
}

export interface FieldSettingsChange {
  fieldKey: keyof FormSettings;
  value: boolean;
}

export interface FieldPropertiesPanelProps {
  spec: FormSpec | null;
  selectedKey: string | null;
  onChange: (change: FieldPropertiesChange) => void;
  onFieldNameChange: (change: FieldNameChange) => void;
  onSettingsChange: (change: FieldSettingsChange) => void;
  onShowToml: () => void;
}

interface ValidationGroupProps {
  value: boolean | undefined;
  label: string;
  fieldKey: keyof FormField;
  fieldName: string;
  onChange: (change: FieldPropertiesChange) => void;
  description?: string;
  disabled?: boolean;
}

function ValidationGroup({
  value,
  label,
  fieldKey,
  fieldName,
  onChange,
  description,
  disabled,
}: ValidationGroupProps) {
  const color = value === true ? 'green' : value === false ? 'red' : 'gray';
  const icon =
    value === true ? <IconCircleCheck /> : value === false ? <IconCircleX /> : <IconCircle />;

  return (
    <>
      <ButtonGroup>
        <Button
          onClick={() => onChange({ fieldName, fieldKey, value: !value })}
          variant="light"
          size="xs"
          color={color}
          leftSection={icon}
          disabled={disabled}
        >
          {label}
        </Button>
        <Button
          onClick={() => onChange({ fieldName, fieldKey, value: undefined })}
          variant="light"
          size="xs"
          disabled={value === undefined}
        >
          <IconCircleMinus />
        </Button>
      </ButtonGroup>
      {description && (
        <Text size="xs" c="dimmed" mt="xs">
          {description}
        </Text>
      )}
    </>
  );
}

function FieldPreview({ field }: { field: FormField }) {
  const { t } = useTranslation();
  const fieldHtml = useMemo(() => renderInputField(field), [field]);

  return (
    <Box>
      <Text size="sm" fw="bold" mb="xs">
        {t('specField.htmlPreview')}
      </Text>
      <Text
        size="xs"
        p="sm"
        className="rusty-form"
        style={{ border: '1px solid var(--mantine-color-gray-2)', borderRadius: '5px' }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fieldHtml) }}
      />
      <Text size="sm" fw="bold" mt="md" mb="xs">
        {t('specField.htmlCode')}
      </Text>
      <Box style={{ border: '1px solid var(--mantine-color-gray-2)', borderRadius: '5px' }}>
        <CodeEditor value={fieldHtml} language="html" readOnly />
      </Box>
    </Box>
  );
}

interface FieldFormProps {
  fieldKey: string;
  field: FormField;
  spec: FormSpec | null;
  onChange: (change: FieldPropertiesChange) => void;
  onFieldNameChange: (change: FieldNameChange) => void;
}

function FieldForm({ fieldKey, field, spec, onChange, onFieldNameChange }: FieldFormProps) {
  const { t } = useTranslation();
  const fieldName = fieldKey;
  const meta = getFieldTypeMeta(field.field);

  const hasOptions = !!meta?.hasOptions;
  const hasPlaceholder = !!meta?.hasPlaceholder;

  const [optionsArray, setOptionsArray] = useState<string[]>([]);
  const [newOption, setNewOption] = useState<string>('');
  const [showMoreOptions, setShowMoreOptions] = useState<boolean>(false);

  const [localFieldName, setLocalFieldName] = useState<string>(field.name);
  const [localPlaceholder, setLocalPlaceholder] = useState<string>(field.placeholder || '');
  const [localHelpText, setLocalHelpText] = useState<string>(field.helptext || '');

  const [debouncedFieldName] = useDebouncedValue(localFieldName, 300);
  const [debouncedPlaceholder] = useDebouncedValue(localPlaceholder, 300);
  const [debouncedHelpText] = useDebouncedValue(localHelpText, 300);

  const [prevFieldKey, setPrevFieldKey] = useState(fieldKey);

  // Reset local state only when switching fields
  useEffect(() => {
    if (fieldKey !== prevFieldKey) {
      setLocalFieldName(field.name);
      setLocalPlaceholder(field.placeholder || '');
      setLocalHelpText(field.helptext || '');
      setPrevFieldKey(fieldKey);
    }
  }, [fieldKey, prevFieldKey, field.name, field.placeholder, field.helptext]);

  const onFailOptions = [
    { value: 'spam', label: t('specField.onFail.spam') },
    { value: 'trash', label: t('specField.onFail.trash') },
    { value: 'reject', label: t('specField.onFail.reject') },
    { value: 'pass', label: t('specField.onFail.pass') },
  ];

  useEffect(() => {
    if (field.options) {
      setOptionsArray(
        field.options
          .split(',')
          .map((option) => option.trim())
          .filter(Boolean)
      );
    } else {
      setOptionsArray([]);
    }
  }, [field.options]);

  useEffect(() => {
    if (debouncedFieldName !== field.name) {
      onChange({ fieldName, fieldKey: 'name', value: debouncedFieldName });
      onFieldNameChange({ oldFieldName: fieldName, newFieldName: debouncedFieldName });
    }
  }, [debouncedFieldName]);

  useEffect(() => {
    if (debouncedPlaceholder !== field.placeholder) {
      onChange({ fieldName, fieldKey: 'placeholder', value: debouncedPlaceholder });
    }
  }, [debouncedPlaceholder]);

  useEffect(() => {
    if (debouncedHelpText !== field.helptext) {
      onChange({ fieldName, fieldKey: 'helptext', value: debouncedHelpText });
    }
  }, [debouncedHelpText]);

  const handleAddOption = () => {
    if (newOption && !optionsArray.includes(newOption)) {
      const updated = [...optionsArray, newOption];
      onChange({ fieldName, fieldKey: 'options', value: updated.join(',') });
      setNewOption('');
    }
  };

  const handleRemoveOption = (option: string) => {
    const updated = optionsArray.filter((o) => o !== option);
    onChange({ fieldName, fieldKey: 'options', value: updated.join(',') });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && newOption) {
      event.preventDefault();
      handleAddOption();
    }
  };

  const handleFieldNameChange = (newName: string) => {
    const sanitized = newName.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    setLocalFieldName(sanitized);
  };

  const handleTypeChange = (newType: string) => {
    if (!isValidFieldType(newType) || newType === field.field) {
      onChange({ fieldName, fieldKey: 'field', value: newType });
      return;
    }

    const newMeta = getFieldTypeMeta(newType);
    let dropped = false;

    onChange({ fieldName, fieldKey: 'field', value: newType });

    // Drop incompatible options
    if (!newMeta?.hasOptions && field.options) {
      onChange({ fieldName, fieldKey: 'options', value: undefined });
      dropped = true;
    }
    if (!newMeta?.hasPlaceholder && field.placeholder) {
      onChange({ fieldName, fieldKey: 'placeholder', value: undefined });
      setLocalPlaceholder('');
      dropped = true;
    }

    // Re-derive validation flags for email / url types
    if (newType === 'email') {
      onChange({ fieldName, fieldKey: 'is_email', value: true });
      onChange({ fieldName, fieldKey: 'is_url', value: undefined });
    } else if (newType === 'url') {
      onChange({ fieldName, fieldKey: 'is_url', value: true });
      onChange({ fieldName, fieldKey: 'is_email', value: undefined });
    } else {
      if (field.is_email) {
        onChange({ fieldName, fieldKey: 'is_email', value: undefined });
        dropped = true;
      }
      if (field.is_url) {
        onChange({ fieldName, fieldKey: 'is_url', value: undefined });
        dropped = true;
      }
    }

    // file fields force on_fail to pass on backend; reset to safe default
    if (newType === 'file' && field.on_fail && field.on_fail !== 'pass') {
      onChange({ fieldName, fieldKey: 'on_fail', value: 'pass' });
      dropped = true;
    }

    if (dropped) {
      notifications.show({
        color: 'yellow',
        title: t('fieldEditor.typeChangedTitle'),
        message: t('fieldEditor.typeChangedMessage'),
        autoClose: 5000,
      });
    }
  };

  const typeData = FIELD_TYPES.map((m) => ({ value: m.type, label: t(m.labelKey) }));

  return (
    <Stack gap="md">
      <Box>
        <Stack gap="sm">
          <TextInput
            label={t('specField.labelField')}
            placeholder={t('specField.labelPlaceholder')}
            value={field.label || ''}
            onChange={(event) =>
              onChange({ fieldName, fieldKey: 'label', value: event.currentTarget.value })
            }
            required
          />

          {hasOptions && (
            <PillsInput
              label={t('specField.options')}
              description={t('specField.optionsDescription')}
              required
            >
              <Pill.Group>
                {optionsArray.map((option, index) => (
                  <Pill key={index} withRemoveButton onRemove={() => handleRemoveOption(option)}>
                    {option}
                  </Pill>
                ))}
                <PillsInput.Field
                  placeholder={t('specField.addOptionPlaceholder')}
                  value={newOption}
                  onChange={(e) => setNewOption(e.currentTarget.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={() => newOption && handleAddOption()}
                />
              </Pill.Group>
            </PillsInput>
          )}

          <TextInput
            label={t('specField.fieldName')}
            placeholder={t('specField.fieldNamePlaceholder')}
            value={localFieldName}
            onChange={(event) => handleFieldNameChange(event.currentTarget.value)}
            description={t('specField.fieldNameDescription')}
            required
            size="xs"
          />

          <NativeSelect
            label={t('specField.fieldType')}
            value={field.field}
            data={typeData}
            onChange={(event) => handleTypeChange(event.currentTarget.value as FieldType)}
            required
            size="xs"
          />

          {hasPlaceholder && (
            <TextInput
              label={t('specField.placeholder')}
              placeholder={t('specField.placeholderDescription')}
              value={localPlaceholder}
              onChange={(event) => setLocalPlaceholder(event.currentTarget.value)}
              size="xs"
            />
          )}

          <TextInput
            label={t('specField.helpText')}
            placeholder={t('specField.helpTextPlaceholder')}
            value={localHelpText}
            onChange={(event) => setLocalHelpText(event.currentTarget.value)}
            size="xs"
          />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Text size="sm" fw="bold" mb="sm">
          {t('specField.validation')}
        </Text>
        <Stack gap="sm">
          <Box>
            <ValidationGroup
              value={field.required}
              label={t('specField.required')}
              fieldKey="required"
              fieldName={fieldName}
              onChange={onChange}
              description={t('specField.requiredDescription')}
            />
          </Box>

          <Button
            variant="subtle"
            size="sm"
            leftSection={
              showMoreOptions ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />
            }
            onClick={() => setShowMoreOptions(!showMoreOptions)}
            mt="sm"
          >
            {t('specField.moreOptionsAndPreview')}
          </Button>

          <Collapse in={showMoreOptions}>
            <Stack gap="sm" mt="sm">
              <NativeSelect
                label={t('specField.onValidationFailure')}
                value={field.on_fail || 'pass'}
                data={onFailOptions}
                onChange={(event) =>
                  onChange({
                    fieldName,
                    fieldKey: 'on_fail',
                    value: event.currentTarget.value,
                  })
                }
                disabled={field.field === 'file'}
                description={t('specField.onValidationFailureDescription')}
              />

              {(() => {
                const otherSpamField = spec
                  ? Object.entries(spec.fields).find(([key, f]) => key !== fieldKey && f.check_spam)
                  : null;
                const lockOutBecauseOther = !field.check_spam && !!otherSpamField;
                return (
                  <>
                    <ValidationGroup
                      value={field.check_spam}
                      label={t('specField.checkSpam')}
                      fieldKey="check_spam"
                      fieldName={fieldName}
                      onChange={onChange}
                      description={t('specField.checkSpamDescription')}
                      disabled={lockOutBecauseOther}
                    />
                    {lockOutBecauseOther && otherSpamField && (
                      <Alert
                        variant="light"
                        color="gray"
                        icon={<IconShieldCheck size={16} />}
                        mt="xs"
                      >
                        {t('specField.checkSpamSingletonNotice', {
                          field: otherSpamField[1].name || otherSpamField[0],
                        })}
                      </Alert>
                    )}
                    {field.check_spam && (
                      <Alert
                        variant="light"
                        color="teal"
                        icon={<IconShieldCheck size={16} />}
                        mt="xs"
                      >
                        {t('specField.checkSpamActiveNotice')}
                      </Alert>
                    )}
                  </>
                );
              })()}

              <Divider />

              <FieldPreview field={field} />

              <Text size="xs" mt="sm" c="dimmed">
                {t('specField.optionsNote')}
              </Text>
            </Stack>
          </Collapse>
        </Stack>
      </Box>
    </Stack>
  );
}

interface GlobalSettingsCardProps {
  spec: FormSpec | null;
  onSettingsChange: (change: FieldSettingsChange) => void;
  onShowToml: () => void;
}

function GlobalSettingsCard({ spec, onSettingsChange, onShowToml }: GlobalSettingsCardProps) {
  const { t } = useTranslation();
  return (
    <Stack gap="md">
      <Title order={5}>{t('formSpec.editor.formSettings')}</Title>
      <Text size="sm" c="dimmed">
        {t('formSpec.editor.selectFieldToEdit')}
      </Text>

      <Title order={5} mt="sm">
        {t('formSpec.editor.globalSettings')}
      </Title>
      <Checkbox
        label={t('formSpec.editor.discardAdditionalFields')}
        checked={spec?.settings?.discard_additional_fields || false}
        onChange={(event) =>
          onSettingsChange({
            fieldKey: 'discard_additional_fields',
            value: event.currentTarget.checked,
          })
        }
        description={t('formSpec.editor.discardAdditionalFieldsDescription')}
      />

      <Anchor component="button" type="button" size="sm" onClick={onShowToml}>
        {t('fieldEditor.viewToml')}
      </Anchor>
    </Stack>
  );
}

export function FieldPropertiesPanel({
  spec,
  selectedKey,
  onChange,
  onFieldNameChange,
  onSettingsChange,
  onShowToml,
}: FieldPropertiesPanelProps) {
  const field = selectedKey && spec?.fields ? spec.fields[selectedKey] : null;

  return (
    <ScrollArea h="100%" type="auto">
      <Box p="md">
        {field && selectedKey ? (
          <FieldForm
            fieldKey={selectedKey}
            field={field}
            spec={spec}
            onChange={onChange}
            onFieldNameChange={onFieldNameChange}
          />
        ) : (
          <GlobalSettingsCard
            spec={spec}
            onSettingsChange={onSettingsChange}
            onShowToml={onShowToml}
          />
        )}
      </Box>
    </ScrollArea>
  );
}
